// Platform Operations dashboard — the operator landing. Built entirely on live
// platform endpoints (auth /platform/analytics|health|security) plus lightweight
// pending-work counts for the attention queue. This is the platform control plane's
// home: tenant/health/security KPIs + an attention queue + recent sensitive actions —
// NOT the operator's own-tenant employee headcount.
// See docs/admin-platform-operations-console-design.md §8 (P0-5).
import { fmtDate, fmtDateTime } from '../lib/format';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Row, Col, Card, Statistic, Table, Tag, Space, Typography, Spin, Alert, Button, List, Empty } from 'antd';
import {
  ApartmentOutlined, TeamOutlined, SafetyCertificateOutlined, HeartOutlined,
  ReloadOutlined, WarningOutlined, PlusOutlined, ShopOutlined, AuditOutlined, RightOutlined,
} from '@ant-design/icons';
import { Link } from 'react-router-dom';
import {
  getPlatformAnalytics, getPlatformHealth, getPlatformSecurity,
  type PlatformAnalytics, type ServiceHealth, type PlatformSecurity,
} from '../services/platformAnalyticsService';
import { listApplications } from '../services/tenantService';
import { getApplications as getPartnerApplications } from '../services/partnerApplicationService';
import { getPendingProducts } from '../services/creditProductService';
import { getDisbursementStatus } from '../services/paymentsService';
import { getErasureRequests } from '../services/dataGovernanceService';
import { listIncidents } from '../services/incidentService';

const { Title, Text } = Typography;

interface Attention {
  tenantApplications: number;
  partnerApplications: number;
  partnerProducts: number;
  lockedAccounts: number;
  servicesDown: number;
  failedDisbursements: number;
  reconciliationBacklog: number;
  openDsar: number;
  openIncidents: number;
}

const fmtTime = (iso?: string | null) => (iso ? fmtDateTime(iso) : '—');

const DashboardPage: React.FC = () => {
  const [analytics, setAnalytics] = useState<PlatformAnalytics | null>(null);
  const [health, setHealth] = useState<ServiceHealth[]>([]);
  const [security, setSecurity] = useState<PlatformSecurity | null>(null);
  const [attention, setAttention] = useState<Attention | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [refreshedAt, setRefreshedAt] = useState<Date | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      // The three platform endpoints are the source of truth; fail the page only if
      // analytics (the core) fails. Health/security degrade gracefully.
      const [a, h, s] = await Promise.all([
        getPlatformAnalytics(),
        getPlatformHealth().catch(() => ({ services: [] as ServiceHealth[] })),
        getPlatformSecurity().catch(() => null),
      ]);
      setAnalytics(a);
      setHealth(h.services || []);
      setSecurity(s);
      // Attention counts — each optional; a missing surface contributes 0, never breaks the page.
      const [ta, pa, pp, disb, dsar, incidents] = await Promise.all([
        listApplications('pending').then((r: any[]) => r?.length ?? 0).catch(() => 0),
        getPartnerApplications('submitted').then((r: any[]) => r?.length ?? 0).catch(() => 0),
        getPendingProducts().then((r: any[]) => r?.length ?? 0).catch(() => 0),
        getDisbursementStatus().catch(() => null),
        getErasureRequests().then((r: any[]) => r?.length ?? 0).catch(() => 0),
        listIncidents().then((r: any[]) => r.filter((i) => i.status !== 'resolved').length).catch(() => 0),
      ]);
      const failedDisbursements = disb
        ? Object.entries(disb.last_30d_counts || {})
            .filter(([k]) => /fail/i.test(k))
            .reduce((sum, [, n]) => sum + (Number(n) || 0), 0)
        : 0;
      setAttention({
        tenantApplications: ta,
        partnerApplications: pa,
        partnerProducts: pp,
        lockedAccounts: s?.totals.lockedAccounts ?? 0,
        servicesDown: (h.services || []).filter((x) => x.status === 'down').length,
        failedDisbursements,
        reconciliationBacklog: disb?.reconciliation_backlog ?? 0,
        openDsar: dsar,
        openIncidents: incidents,
      });
      setRefreshedAt(new Date());
    } catch (e: any) {
      setError(e?.response?.data?.message || 'Could not load platform metrics.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { void load(); }, [load]);

  const servicesUp = health.filter((s) => s.status === 'up').length;
  const servicesDown = health.filter((s) => s.status === 'down').length;
  const servicesProbed = health.filter((s) => s.status !== 'unknown').length;
  // Healthy = nothing actually down; 'unknown' (e.g. mobile, not probed) stays neutral.
  const overallHealthy = health.length > 0 && servicesDown === 0;

  const attentionItems = useMemo(() => {
    if (!attention) return [];
    return [
      { key: 'in', label: 'Open incidents', count: attention.openIncidents, to: '/support/incidents' },
      { key: 'ta', label: 'Tenant applications pending review', count: attention.tenantApplications, to: '/tenants/applications' },
      { key: 'pa', label: 'Partner applications awaiting decision', count: attention.partnerApplications, to: '/credit/partner-applications' },
      { key: 'pp', label: 'Partner products pending approval', count: attention.partnerProducts, to: '/credit/products-review' },
      { key: 'fd', label: 'Failed disbursements (last 30 days)', count: attention.failedDisbursements, to: '/payments' },
      { key: 'rb', label: 'Disbursement reconciliation backlog', count: attention.reconciliationBacklog, to: '/payments' },
      { key: 'ds', label: 'Open data-subject (DSAR) requests', count: attention.openDsar, to: '/compliance/erasure-requests' },
      { key: 'la', label: 'Locked user accounts', count: attention.lockedAccounts, to: '/system/security' },
      { key: 'sd', label: 'Services reporting down', count: attention.servicesDown, to: '/system/monitoring' },
    ].filter((i) => i.count > 0);
  }, [attention]);

  if (loading && !analytics) {
    return (
      <div className="flex items-center justify-center py-24">
        <Spin size="large" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Platform status header */}
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <Title level={3} style={{ marginBottom: 0 }}>Platform Operations</Title>
          <Space size="small" wrap>
            <Tag color={overallHealthy ? 'success' : health.length ? 'error' : 'default'}>
              {overallHealthy ? 'All services operational' : health.length ? `${servicesUp}/${servicesProbed} services up · ${servicesDown} down` : 'Health unknown'}
            </Tag>
            <Text type="secondary">Last refreshed {fmtTime(refreshedAt?.toISOString())}</Text>
          </Space>
        </div>
        <Button icon={<ReloadOutlined />} onClick={() => void load()} loading={loading}>Refresh</Button>
      </div>

      {error && <Alert type="error" showIcon message={error} />}

      {/* Operational KPI cards */}
      <Row gutter={[16, 16]}>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic title="Active tenants" value={analytics?.totals.activeTenants ?? 0} prefix={<ApartmentOutlined />}
              suffix={<Text type="secondary" style={{ fontSize: 12 }}>/ {analytics?.totals.tenants ?? 0}</Text>} />
            {(analytics?.totals.suspendedTenants ?? 0) > 0 && (
              <Text type="warning" style={{ fontSize: 12 }}>{analytics?.totals.suspendedTenants} suspended</Text>
            )}
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic title="Active users" value={analytics?.totals.activeUsers ?? 0} prefix={<TeamOutlined />}
              suffix={<Text type="secondary" style={{ fontSize: 12 }}>/ {analytics?.totals.users ?? 0}</Text>} />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic title="Services up" value={servicesUp} suffix={`/ ${servicesProbed || '—'}`} prefix={<HeartOutlined />} />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic title="MFA adoption" value={security?.totals.mfaAdoptionPct ?? 0} suffix="%" prefix={<SafetyCertificateOutlined />} />
          </Card>
        </Col>
      </Row>

      {/* Attention-required queue */}
      <Card title={<Space><WarningOutlined />Attention required</Space>}>
        {attentionItems.length === 0 ? (
          <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="Nothing needs attention right now." />
        ) : (
          <List
            dataSource={attentionItems}
            renderItem={(i) => (
              <List.Item actions={[<Link key="go" to={i.to}>Review <RightOutlined /></Link>]}>
                <Space><Tag color="warning">{i.count}</Tag>{i.label}</Space>
              </List.Item>
            )}
          />
        )}
      </Card>

      <Row gutter={[16, 16]}>
        {/* Service & integration health */}
        <Col xs={24} lg={12}>
          <Card title="Service health" extra={<Link to="/system/monitoring">Details</Link>}>
            {health.length === 0 ? (
              <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="Health data unavailable." />
            ) : (
              <List
                size="small"
                dataSource={health}
                renderItem={(s) => (
                  <List.Item>
                    <Space><Tag color={s.status === 'up' ? 'success' : s.status === 'down' ? 'error' : 'default'}>{s.status.toUpperCase()}</Tag>{s.name}</Space>
                    <Text type="secondary">{s.status === 'unknown' ? '—' : `${s.responseMs}ms`}</Text>
                  </List.Item>
                )}
              />
            )}
          </Card>
        </Col>

        {/* Recent tenants */}
        <Col xs={24} lg={12}>
          <Card title="Recent tenants" extra={<Link to="/tenants">All tenants</Link>}>
            <Table
              size="small"
              rowKey="id"
              pagination={false}
              dataSource={analytics?.recentTenants ?? []}
              locale={{ emptyText: 'No tenants yet.' }}
              columns={[
                { title: 'Name', dataIndex: 'name', render: (n: string, r: any) => <Link to={`/tenants/${r.id}`}>{n}</Link> },
                { title: 'Slug', dataIndex: 'slug' },
                { title: 'Status', dataIndex: 'isActive', render: (a: boolean) => <Tag color={a ? 'success' : 'default'}>{a ? 'Active' : 'Inactive'}</Tag> },
                { title: 'Created', dataIndex: 'createdAt', render: fmtTime },
              ]}
            />
          </Card>
        </Col>
      </Row>

      {/* Recent sensitive admin actions */}
      <Card title="Recent security activity" extra={<Link to="/system/audit">Audit log</Link>}>
        <Table
          size="small"
          rowKey={(r: any, i) => `${r.at}-${i}`}
          pagination={false}
          dataSource={security?.recentEvents ?? []}
          locale={{ emptyText: 'No recent events.' }}
          columns={[
            { title: 'Action', dataIndex: 'action' },
            { title: 'Who', dataIndex: 'who', render: (w: string | null) => w || '—' },
            { title: 'Tenant', dataIndex: 'tenant', render: (t: string | null) => t || '—' },
            { title: 'IP', dataIndex: 'ip', render: (ip: string | null) => ip || '—' },
            { title: 'When', dataIndex: 'at', render: fmtTime },
          ]}
        />
      </Card>

      {/* Quick actions */}
      <Card title="Quick actions">
        <Space wrap>
          <Link to="/tenants/onboarding"><Button icon={<PlusOutlined />}>Create tenant</Button></Link>
          <Link to="/credit/partner-applications"><Button icon={<ShopOutlined />}>Review partner applications</Button></Link>
          <Link to="/credit/products-review"><Button icon={<ShopOutlined />}>Review products</Button></Link>
          <Link to="/support/incidents"><Button icon={<WarningOutlined />}>Incidents</Button></Link>
          <Link to="/system/audit"><Button icon={<AuditOutlined />}>Audit log</Button></Link>
        </Space>
      </Card>
    </div>
  );
};

export default DashboardPage;
